import type {
  MermaidElementModel,
  MermaidFitMode,
} from '@blocksuite/affine-model';
import type { MermaidRenderSize } from '@blocksuite/affine-shared/services';
import { Bound } from '@blocksuite/global/gfx';

export const MERMAID_FIT_MIN_WIDTH = 320;
export const MERMAID_FIT_MIN_HEIGHT = 200;
export const MERMAID_FIT_MAX_WIDTH = 1200;
export const MERMAID_FIT_MAX_HEIGHT = 900;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function getMermaidFitSize(
  current: Bound,
  mode: Exclude<MermaidFitMode, 'free'>,
  intrinsic: MermaidRenderSize
): MermaidRenderSize {
  const width = Math.max(1, intrinsic.width);
  const height = Math.max(1, intrinsic.height);
  const aspect = width / height;

  if (mode === 'width') {
    let nextWidth = clamp(
      current.w,
      MERMAID_FIT_MIN_WIDTH,
      MERMAID_FIT_MAX_WIDTH
    );
    let nextHeight = nextWidth / aspect;

    if (nextHeight < MERMAID_FIT_MIN_HEIGHT) {
      nextHeight = MERMAID_FIT_MIN_HEIGHT;
      nextWidth = nextHeight * aspect;
    } else if (nextHeight > MERMAID_FIT_MAX_HEIGHT) {
      nextHeight = MERMAID_FIT_MAX_HEIGHT;
      nextWidth = nextHeight * aspect;
    }

    return {
      width: clamp(nextWidth, MERMAID_FIT_MIN_WIDTH, MERMAID_FIT_MAX_WIDTH),
      height: clamp(nextHeight, MERMAID_FIT_MIN_HEIGHT, MERMAID_FIT_MAX_HEIGHT),
    };
  }

  const minScale = Math.max(
    MERMAID_FIT_MIN_WIDTH / width,
    MERMAID_FIT_MIN_HEIGHT / height
  );
  const maxScale = Math.min(
    MERMAID_FIT_MAX_WIDTH / width,
    MERMAID_FIT_MAX_HEIGHT / height
  );
  const scale =
    minScale <= maxScale ? clamp(1, minScale, maxScale) : Math.min(1, maxScale);

  return {
    width: width * scale,
    height: height * scale,
  };
}

export function applyMermaidFitMode(
  model: MermaidElementModel,
  mode: MermaidFitMode,
  intrinsic?: MermaidRenderSize | null
) {
  if (mode === 'free') {
    model.fitMode = 'free';
    return true;
  }
  if (!intrinsic) return false;

  const current = Bound.deserialize(model.xywh);
  const next = getMermaidFitSize(current, mode, intrinsic);
  const center = current.center;

  model.xywh = new Bound(
    center[0] - next.width / 2,
    center[1] - next.height / 2,
    next.width,
    next.height
  ).serialize();
  model.fitMode = mode;
  return true;
}
